import type {PublicMethodUtils} from '@wix/document-services-types'
import type {SavePublicAPI} from '@wix/document-services-public-types'
import {saveAPI} from '@wix/document-services-implementation'
import type {PublicModuleDeclaration} from '../../documentServicesInitialisation'

export default function (publicMethodUtils: PublicMethodUtils): PublicModuleDeclaration<SavePublicAPI> {
    const {
        save,
        publish,
        saveAsTemplate,
        canUserPublish,
        initMethod,
        getLastSaveInfo,
        registerToDeploymentStatus,
        restoreRevision,
        resetRevisionWithoutTransactions
    } = saveAPI
    return {
        initMethod,
        methods: {
            /**
             * Saves the site
             * @member documentServices
             * @param {onSuccess} [onSuccess]
             * @param {onError} [onError]
             * @param {boolean} isFullSave - whether to perform a full save or not
             * @param {object} options
             * @return {Promise} a promise that the save will be performed. This is for syntactic sugar for success/error.
             */
            save: publicMethodUtils.defineDataManipulationAction(save),
            /**
             * Publishes the last saved version of the site. If you want to save the site first, you should call save and then publish.
             * @member documentServices
             * @param {onSuccess} [onSuccess]
             * @param {onError} [onError]
             * @param {boolean} isFullSave - whether to perform a full save or not
             * @return {Promise} a promise that the save will be performed. This is for syntactic sugar for success/error.
             */
            publish: publicMethodUtils.defineDataManipulationAction(publish),

            saveAsTemplate: publicMethodUtils.defineDataManipulationAction(saveAsTemplate),

            /**
             * Returns true if the user is allowed to publish according to server, false otherwise
             * @instance
             * @param {ps} privateServices
             * @returns {boolean}
             */
            canUserPublish: publicMethodUtils.defineGetter(canUserPublish),

            getLastSaveInfo: publicMethodUtils.defineGetter(getLastSaveInfo),

            registerToDeploymentStatus: publicMethodUtils.defineDataManipulationAction(registerToDeploymentStatus),
            registerToRevisionChange: publicMethodUtils.extensionPublicAPI.immediate(
                'revisionManager.registerToRevisionChange'
            ),
            unregisterFromRevisionChange: publicMethodUtils.extensionPublicAPI.immediate(
                'revisionManager.unregisterFromRevisionChange'
            ),
            /**
             * Restores the site to a specific revision
             * @member documentServices
             * @param {object} options - Restore options
             * @param {string} [options.reason] - Reason for restore (for logging)
             * @param {string} [options.revisionId] - Target revision ID. Defaults to current revision.
             * @param {string} [options.branchId] - Target branch ID. Required together with revisionId.
             * @return {Promise<void>} A promise that resolves when restore is complete
             */
            restoreRevision: publicMethodUtils.defineAction(restoreRevision),
            /**
             * Drops all pending transactions and creates a revision without them.
             * Other concurrent users will receive a refresh notification.
             * @member documentServices
             * @param {string} reason - Reason for dropping transactions (for logging)
             * @return {Promise<void>} A promise that resolves when transactions are dropped
             */
            resetRevisionWithoutTransactions: publicMethodUtils.defineAction(resetRevisionWithoutTransactions)
        }
    }
}
